<template>
    <div class="photo-library">
        <!-- 筛选按钮区域 -->
        <div class="status-filter-btns">
            <button v-for="item in statusOptions" :key="item.value"
                :class="['status-btn', { selected: statusFilter === item.value }]" @click="selectStatus(item.value)">
                {{ item.label }}
            </button>
        </div>

        <!-- 照片列表 -->
        <ul class="photo-list" v-infinite-scroll="loadMore" :infinite-scroll-disabled="isLoading || isNoMore"
            :infinite-scroll-distance="50">
            <li :class="['photo-item', { 'is-selected': item.isClicked }]" v-for="(item, i) in photoList" :key="i"
                @click="toggleSelect(item)" @mouseenter="item.isHovered = true" @mouseleave="item.isHovered = false">
                <div class="image-container">
                    <imagePreview :src="item.url" :preview-src="item.originalUrl" :preview-src-list="previewImageList"
                        :current-index="getPreviewIndex(item)" :alt="item.originalName || '图片'" @click.stop />

                    <!-- 图标容器 -->
                    <div class="media-icons-container">
                        <!-- 标签图标 -->
                        <el-tooltip placement="top" effect="light" :content="getTagDescription(item)"
                            v-if="getIsShowTag(item)">
                            <div class="media-tag">
                                <img :src="getTagIcon(item)" class="tag-icon" />
                            </div>
                        </el-tooltip>
                        <!-- 是否发送过 -->
                        <el-tooltip placement="top" effect="light" :content="'发送过'" v-if="item.casualStatus == 'sent'">
                            <div class="media-tagSec">
                                <i class="el-icon-s-promotion" style="color: #fff;font-size: 18px;"></i>
                            </div>
                        </el-tooltip>
                        <!-- 是否查看过 -->
                        <el-tooltip placement="top" effect="light" :content="'查看过'"
                            v-if="item.casualStatus == 'accessed'">
                            <div class="media-tagSec" style="background: rgba(255, 255, 255, 0.7);">
                                <img src="@/assets/see.png" style="width: 14px;">
                            </div>
                        </el-tooltip>
                        <!-- 重新审核按钮 -->
                        <el-tooltip placement="top" effect="light" :content="'重新审核'"
                            v-if="shouldShowReReview(item) && item.isHovered">
                            <div class="media-tagSec" @click="reReview(item)">
                                <i class="el-icon-refresh-right" style="color: #fff;font-size: 18px;"></i>
                            </div>
                        </el-tooltip>
                    </div>
                </div>

                <!-- 选中状态指示 -->
                <div class="selection-indicator" v-if="item.isClicked">
                    <i class="el-icon-success" style="color: #5FAAFF;"></i>
                </div>
            </li>

            <!-- 加载状态 -->
            <li v-if="isLoading" class="loading-item">
                <img class="loading-icon" src="@/assets/loading.gif" alt="">
            </li>

            <!-- 无更多数据 -->
            <li v-if="isNoMore && !isLoading" class="no-more-item">
                没有更多了
            </li>
        </ul>
    </div>
</template>

<script>
import { getGalleryPhotos, sendForModeration } from '@/api/talkytimes/message'
import imagePreview from '@/components/imagePreviewForAttachment.vue'
import { deletePhoto } from '@/api/talkytimes/attachment'

export default {
    name: 'ManagementPhotoLibrary',
    components: { imagePreview },
    props: {
        chatUserInfo: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            photoList: [],
            statusFilter: 'all',
            statusOptions: [
                { label: 'All', value: 'all' },
                { label: 'Approved', value: 'approved' },
                { label: 'Temporary', value: 'temporary' },
                { label: 'Special', value: 'special' },
                { label: 'Special +', value: 'special_plus' },
                { label: 'Declined', value: 'declined' },
            ],
            limit: 50,
            cursor: '',
            isLoading: false,
            isNoMore: false,
            hasError: false
        }
    },
    computed: {
        previewImageList() {
            return this.photoList.map(item => item.originalUrl || item.url)
        }
    },
    watch: {
        statusFilter: {
            handler() {
                this.refresh()
            }
        },
        chatUserInfo: {
            handler(newVal, oldVal) {
                if (newVal && oldVal && newVal.userId !== oldVal.userId) {
                    this.refresh()
                }
            },
            deep: true
        }
    },
    mounted() {
        this.loadPhotos()
    },
    methods: {
        async loadPhotos(isLoadMore = false) {
            if (this.isLoading) return
            if (this.hasError && !isLoadMore) return

            this.isLoading = true
            this.hasError = false

            const idUser = this.chatUserInfo.userId || this.chatUserInfo.idInterlocutor || this.chatUserInfo.id_user || this.chatUserInfo.id

            if (!idUser) {
                console.error('无法获取有效的用户ID')
                this.isLoading = false
                this.hasError = true
                return
            }

            const params = {
                cursor: isLoadMore ? String(this.cursor) : "",
                limit: this.limit,
                idUser: idUser
            }

            // 根据筛选条件设置参数
            if (this.statusFilter === 'all') {
                params.statuses = []
                params.tags = []
            } else if (this.statusFilter === 'approved') {
                params.statuses = ['approved', 'approved_by_ai']
                params.tags = []
            } else if (this.statusFilter === 'temporary') {
                params.isTemporary = true
                params.statuses = []
            } else if (this.statusFilter === 'special') {
                params.statuses = ['approved', 'approved_by_ai']
                params.tags = ['special']
            } else if (this.statusFilter === 'special_plus') {
                params.statuses = ['approved', 'approved_by_ai']
                params.tags = ['special_plus']
            } else if (this.statusFilter === 'declined') {
                params.statuses = ['declined', 'declined_by_ai']
                params.tags = []
            }

            const res = await getGalleryPhotos(params)

            const photoArr = Array.isArray(res.photos) ? res.photos : []
            const newPhotos = photoArr.map(item => ({
                ...item,
                url: item.urls?.urlPreview || item.url,
                originalUrl: item.urls?.urlOriginal || item.originalUrl,
                originalName: item.filename || '图片',
                fileCateType: 1,
                isClicked: false,
                isHovered: false,
                tags: item.tags || [],
                status: typeof item.status === 'string' ? { code: item.status } : item.status,
                casualStatus: item.casualStatus
            }))

            if (isLoadMore) {
                this.photoList = this.photoList.concat(newPhotos)
            } else {
                this.photoList = newPhotos
            }

            this.cursor = res.cursor || ''
            this.isNoMore = newPhotos.length < this.limit

            this.isLoading = false
        },

        loadMore() {
            if (!this.isLoading && !this.isNoMore && !this.hasError) {
                this.loadPhotos(true)
            }
        },

        refresh() {
            this.cursor = ''
            this.isNoMore = false
            this.hasError = false
            this.photoList = []
            this.loadPhotos()
        },

        toggleSelect(item) {
            if (!this.canSelect(item)) return

            this.$set(item, 'isClicked', !item.isClicked)

            if (item.isClicked) {
                this.$emit('item-selected', item)
            } else {
                this.$emit('item-deselected', item)
            }
        },

        canSelect(item) {
            return true // 管理模式下都可以选择
        },

        selectStatus(value) {
            this.statusFilter = value
        },

        getPreviewIndex(item) {
            return this.photoList.findIndex(photo => photo.idPhoto === item.idPhoto)
        },

        getIsShowTag(item) {
            if (!item) return false
            const statusValue = typeof item.status === 'string' ? item.status : (item.status?.code || '')
            if (statusValue && statusValue.includes('approved')) {
                return item.tags && Array.isArray(item.tags) && item.tags.length > 0
            }
            return false
        },

        getTagIcon(item) {
            if (!item) return ''
            const statusValue = typeof item.status === 'string' ? item.status : (item.status?.code || '')
            if (statusValue && statusValue.includes('approved')) {
                if (item.tags && Array.isArray(item.tags) && item.tags.length > 0) {
                    const tagCode = typeof item.tags[0] === 'string' ? item.tags[0] : (item.tags[0]?.code || '')
                    switch (tagCode) {
                        case 'special_plus':
                            return require('@/assets/svg/special+.svg')
                        case 'pre_special':
                            return require('@/assets/svg/special-.svg')
                        case 'special':
                            return require('@/assets/svg/special.svg')
                        default:
                            return ''
                    }
                }
            } else if (statusValue && statusValue.includes('declined')) {
                return require('@/assets/svg/negative.svg')
            }
            return ''
        },

        getTagDescription(item) {
            if (!item) return ''
            const statusValue = typeof item.status === 'string' ? item.status : (item.status?.code || '')
            if (statusValue && statusValue.includes('approved')) {
                if (item.tags && Array.isArray(item.tags) && item.tags.length > 0) {
                    const tagCode = typeof item.tags[0] === 'string' ? item.tags[0] : (item.tags[0]?.code || '')
                    switch (tagCode) {
                        case 'special_plus':
                            return '特殊+内容'
                        case 'pre_special':
                            return 'AI预设'
                        case 'special':
                            return '特殊内容'
                        default:
                            return ''
                    }
                }
            } else if (statusValue && statusValue.includes('declined')) {
                return '审核不通过'
            }
            return ''
        },

        shouldShowReReview(item) {
            if (!item) return false
            const statusValue = typeof item.status === 'string' ? item.status : (item.status?.code || '')
            if (!statusValue) return false
            if (statusValue.includes('declined')) return true
            return false
        },

        async reReview(item) {
            await sendForModeration({ idPhoto: item.idPhoto })
            this.$message.success('重新提审成功')
            this.refresh()
        },

        async deleteItems(items) {
            if (!items || items.length === 0) return

            const itemIds = items.map(item => item.idPhoto || item.id)
            await deletePhoto({ idsPhotos: itemIds })

            this.photoList = this.photoList.filter(photo => {
                const photoId = photo.idPhoto || photo.id
                return !itemIds.includes(photoId)
            })

            this.$message.success(`成功删除 ${items.length} 张照片`)
        }
    }
}
</script>

<style lang="scss" scoped>
.photo-library {
    height: 100%;
    display: flex;
    flex-direction: column;
}

.status-filter-btns {
    display: flex;
    gap: 16px;
    margin: 16px 0;
    padding: 0 8px;

    .status-btn {
        padding: 4px 16px;
        border: none;
        border-radius: 16px;
        background: #f7f9fa;
        color: #333;
        font-size: 15px;
        font-weight: 500;
        cursor: pointer;
        transition: background 0.2s, color 0.2s;

        &.selected {
            background: #eef2ff;
            color: #2346d4;
        }
    }
}

.photo-list {
    flex: 1;
    height: 100%;
    width: 100%;
    display: block;
    padding: 0;
    margin: 0;
    list-style: none;
    overflow-y: auto;

    .photo-item {
        position: relative;
        display: inline-block;
        margin: 3px;
        padding: 10px;
        vertical-align: top;
        box-sizing: border-box;
        text-align: center;
        border: 1px solid transparent;
        transition: all 0.2s;
        cursor: pointer;

        &.is-selected {
            border: 1px solid #2499FF;
            border-radius: 8px;
        }

        .image-container {
            width: 110px;
            height: 110px;
            position: relative;
            display: inline-block;

            ::v-deep .imagePreview {
                width: 110px;
                height: 110px;
                display: block;
            }
        }

        .media-icons-container {
            position: absolute;
            bottom: 0;
            left: 8px;
            display: flex;
            align-items: center;
            gap: 4px;
            z-index: 10;
        }

        .media-tag,
        .media-tagSec {
            position: relative;
            background: rgba(20, 20, 20, 0.4);
            color: white;
            width: 20px;
            height: 20px;
            border-radius: 50px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;

            .tag-icon {
                width: 18px;
                height: 18px;
            }
        }

        .selection-indicator {
            position: absolute;
            right: 0;
            top: 2px;
        }
    }

    .loading-item,
    .no-more-item {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 40px;
        width: 100%;
        color: #999;
        font-size: 14px;

        .loading-icon {
            width: 30px;
            height: 30px;
            animation: rotate360 1s linear infinite;
        }
    }
}

@keyframes rotate360 {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}
</style>
